/**
 * DEV Instance Access Service
 * Manages which users can see DEV instances
 * Configuration loaded from public/config.json
 */

class DevInstanceAccessService {
  constructor() {
    this.config = null;
    this.isLoaded = false;
  }

  /**
   * Load configuration from config.json
   */
  async loadConfig() {
    if (this.isLoaded) {
      return this.config;
    }

    try {
      console.log('📝 [DevInstanceAccessService] Loading DEV instance access configuration...');
      
      const response = await fetch('/config.json', { cache: 'no-store' });
      if (response.ok) {
        const configData = await response.json();
        this.config = configData;
        console.log('✅ [DevInstanceAccessService] Configuration loaded from config.json');
        this.isLoaded = true;
        return this.config;
      }
      
      console.warn('⚠️ [DevInstanceAccessService] config.json not found, allowing all users to see DEV instances');
      // hubBetaAccess fails closed (enabled + empty allowlist) rather than open,
      // since that gate is meant to stay restrictive until explicitly rolled out.
      this.config = { devInstanceAccess: { allowedEmails: [] }, hubBetaAccess: { enabled: true, allowedEmails: [] } };
      this.isLoaded = true;
      return this.config;

    } catch (error) {
      console.error('❌ [DevInstanceAccessService] Error loading configuration:', error);
      this.config = { devInstanceAccess: { allowedEmails: [] }, hubBetaAccess: { enabled: true, allowedEmails: [] } };
      this.isLoaded = true;
      return this.config;
    }
  }

  /**
   * Check if a user has access to DEV instances
   * @param {string} email - User's email address
   * @returns {Promise<boolean>} True if user can see DEV instances
   */
  async canAccessDevInstances(email) {
    if (!this.isLoaded) {
      await this.loadConfig();
    }

    // If no config or empty allowedEmails array, allow all users
    if (!this.config?.devInstanceAccess?.allowedEmails || 
        this.config.devInstanceAccess.allowedEmails.length === 0) {
      console.log('📋 [DevInstanceAccessService] No restrictions configured, allowing all users');
      return true;
    }

    const allowedEmails = this.config.devInstanceAccess.allowedEmails.map(e => e.toLowerCase());
    const userEmail = email.toLowerCase().trim();
    
    const hasAccess = allowedEmails.includes(userEmail);
    
    console.log(`🔐 [DevInstanceAccessService] User ${userEmail} DEV access: ${hasAccess ? '✅ Allowed' : '❌ Denied'}`);
    
    return hasAccess;
  }

  /**
   * Check if a user can see the Adapt Studio hub page (/home).
   * Users in the hubBetaAccess.blocklist are EXCLUDED from the hub and sent
   * to /instances instead. Everyone else sees the hub.
   * @param {string} email - User's email address
   * @returns {Promise<boolean>} True if the user can see the hub page
   */
  async canAccessHubPage(email) {
    if (!this.isLoaded) {
      await this.loadConfig();
    }

    const hubConfig = this.config?.hubBetaAccess;

    // No config, or the beta gate has been turned off - open to everyone
    if (!hubConfig || hubConfig.enabled === false) {
      return true;
    }

    const blockedEmails = (hubConfig.allowedEmails || []).map(e => e.toLowerCase());
    const userEmail = (email || '').toLowerCase().trim();

    // Reverse logic: if user is in the blocklist, they CANNOT see the hub
    const isBlocked = blockedEmails.includes(userEmail);
    const hasAccess = !isBlocked;

    console.log(`🔐 [DevInstanceAccessService] User ${userEmail} hub access: ${hasAccess ? '✅ Can see hub' : '❌ Blocked, goes to /instances'}`);

    return hasAccess;
  }

  /**
   * Resolve where a user should land right after login.
   * @param {string} email - User's email address
   * @returns {Promise<'/home' | '/instances'>}
   */
  async getPostLoginRoute(email) {
    const hasHubAccess = await this.canAccessHubPage(email);
    return hasHubAccess ? '/home' : '/instances';
  }

  /**
   * Get list of allowed emails
   * @returns {Promise<Array<string>>} Array of allowed email addresses
   */
  async getAllowedEmails() {
    if (!this.isLoaded) {
      await this.loadConfig();
    }
    
    return this.config?.devInstanceAccess?.allowedEmails || [];
  }
}

const devInstanceAccessService = new DevInstanceAccessService();
export default devInstanceAccessService;
